import { CSRF_HEADER, type OpenWikiThreadRequest, type WikiThread } from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api.ts";
import { ok } from "../api-errors.ts";
import { useSession } from "../session.tsx";
import { wikiKeys } from "./queries.ts";

// Review threads on a change set and its items (wiki-collaboration D13–D16).

export const threadKeys = {
  set: (projectId: string, id: string) => [...wikiKeys.all(projectId), "threads", id] as const,
  revisions: (projectId: string, conversationId: string) =>
    [...wikiKeys.all(projectId), "revision-requests", conversationId] as const,
};

/** The set's threads as the viewer sees them; an N-1 API has none (404 reads as empty). */
export function useThreads(projectId: string, id: string, enabled = true) {
  return useQuery({
    queryKey: threadKeys.set(projectId, id),
    enabled,
    retry: false,
    queryFn: async (): Promise<WikiThread[]> =>
      (
        await ok(
          await api.api.projects[":projectId"].wiki["change-sets"][":id"].threads.$get({
            param: { projectId, id },
          }),
        )
      ).threads,
  });
}

export type ThreadWrite =
  | { kind: "open"; request: OpenWikiThreadRequest }
  | { kind: "reply"; threadId: string; body: string }
  | { kind: "resolve"; threadId: string; note?: string | undefined }
  | { kind: "reopen"; threadId: string }
  | { kind: "delete"; threadId: string; commentId: string };

/** Every thread write answers the set's threads; the set and the queue are refreshed too. */
export function useThreadWrite(projectId: string, id: string) {
  const queryClient = useQueryClient();
  const { csrfToken } = useSession();
  const headers = { [CSRF_HEADER]: csrfToken };
  return useMutation({
    mutationFn: async (write: ThreadWrite): Promise<WikiThread[]> => {
      const route = api.api.projects[":projectId"].wiki["change-sets"][":id"].threads;
      const param = { projectId, id };
      if (write.kind === "open") {
        return (await ok(await route.$post({ param, json: write.request }, { headers }))).threads;
      }
      const thread = route[":threadId"];
      const p = { ...param, threadId: write.threadId };
      if (write.kind === "reply") {
        return (
          await ok(
            await thread.comments.$post({ param: p, json: { body: write.body } }, { headers }),
          )
        ).threads;
      }
      if (write.kind === "delete") {
        return (
          await ok(
            await thread.comments[":commentId"].$delete(
              { param: { ...p, commentId: write.commentId } },
              { headers },
            ),
          )
        ).threads;
      }
      if (write.kind === "resolve") {
        // The decision note (D16) rides as the body; an N-1 API ignores it.
        return (
          await ok(
            await thread.resolve.$post(
              { param: p, json: write.note ? { note: write.note } : {} },
              { headers },
            ),
          )
        ).threads;
      }
      return (await ok(await thread.reopen.$post({ param: p }, { headers }))).threads;
    },
    onSuccess: (threads) => {
      queryClient.setQueryData(threadKeys.set(projectId, id), threads);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [...wikiKeys.all(projectId), "change", id] });
      void queryClient.invalidateQueries({ queryKey: wikiKeys.review(projectId) });
      // A request for the agent opened or resolved here changes the conversation's cards.
      void queryClient.invalidateQueries({
        queryKey: [...wikiKeys.all(projectId), "revision-requests"],
      });
    },
  });
}

/** The conversation's Revision requested cards (D15), for its creator; none on an N-1 API. */
export function useRevisionRequests(
  projectId: string | null,
  conversationId: string,
  enabled = true,
) {
  return useQuery({
    queryKey: threadKeys.revisions(projectId ?? "", conversationId),
    enabled: enabled && projectId !== null,
    retry: false,
    queryFn: async () =>
      (
        await ok(
          await api.api.projects[":projectId"].wiki["revision-requests"].$get({
            param: { projectId: projectId ?? "" },
            query: { conversationId },
          }),
        )
      ).requests,
  });
}
